import { useEffect, useMemo, useState } from "react";
import { Edit3, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const DEFAULT_ITEM = { nome_cliente: "", cid: "", operadora_id: "", aparelho_fabricante: "", aparelho_modelo: "", sip_sanitize_profile: "" };
const DEFAULT_FORM = { nome_lista: "", itens: [DEFAULT_ITEM], observacao: "", enabled: true };

function normalizeItems(items, operadoras) {
  const operadoraMap = Object.fromEntries((operadoras || []).map((item) => [item.id, item]));
  const unique = new Map();
  for (const rawItem of items || []) {
    const nome_cliente = String(rawItem?.nome_cliente || "").trim();
    const cid = String(rawItem?.cid || "").replace(/\D/g, "");
    const operadora_id = String(rawItem?.operadora_id || "").trim();
    if (!nome_cliente || !cid) continue;
    const operadora = operadoraMap[operadora_id];
    unique.set(cid, {
      nome_cliente,
      cid,
      operadora_id: operadora_id || "",
      operadora_nome: operadora?.nome || String(rawItem?.operadora_nome || "").trim() || "",
      aparelho_fabricante: String(rawItem?.aparelho_fabricante || "").trim(),
      aparelho_modelo: String(rawItem?.aparelho_modelo || "").trim(),
      sip_sanitize_profile: String(rawItem?.sip_sanitize_profile || "").trim(),
    });
  }
  return Array.from(unique.values());
}

export default function CIDAuthorizations() {
  const [rows, setRows] = useState([]);
  const [operadoras, setOperadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cidResponse, opResponse] = await Promise.all([
        api.get("/cid-authorizations"),
        api.get("/operadoras"),
      ]);
      setRows(cidResponse.data || []);
      setOperadoras((opResponse.data || []).filter((item) => item.enabled !== false));
      setError("");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Nao foi possivel carregar as listas de CID.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const operadoraOptions = useMemo(
    () => [{ value: "", label: "Usar rota padrao" }, ...(operadoras || []).map((item) => ({ value: item.id, label: item.nome || item.host || item.id }))],
    [operadoras],
  );

  const open = (row = null) => {
    setEditing(row);
    setError("");
    setForm(row ? {
      nome_lista: row.nome_lista || "",
      itens: Array.isArray(row.itens) && row.itens.length
        ? row.itens.map((item) => ({
          nome_cliente: item.nome_cliente || "",
          cid: item.cid || "",
          operadora_id: item.operadora_id || "",
          aparelho_fabricante: item.aparelho_fabricante || "",
          aparelho_modelo: item.aparelho_modelo || "",
          sip_sanitize_profile: item.sip_sanitize_profile || "",
        }))
        : [{ ...DEFAULT_ITEM }],
      observacao: row.observacao || "",
      enabled: row.enabled !== false,
    } : { ...DEFAULT_FORM, itens: [{ ...DEFAULT_ITEM }] });
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditing(null);
  };

  const updateItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      itens: (current.itens || []).map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: key === "cid" ? String(value || "").replace(/\D/g, "") : value } : item
      )),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      itens: [...(current.itens || []), { ...DEFAULT_ITEM }],
    }));
  };

  const removeItem = (index) => {
    setForm((current) => {
      const nextItems = (current.itens || []).filter((_, itemIndex) => itemIndex !== index);
      return { ...current, itens: nextItems.length ? nextItems : [{ ...DEFAULT_ITEM }] };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const itens = normalizeItems(form.itens, operadoras);
      if (!String(form.nome_lista || "").trim()) {
        throw new Error("Informe o nome da lista.");
      }
      if (!itens.length) {
        throw new Error("Adicione pelo menos um cliente com CID valido.");
      }
      const payload = {
        nome_lista: String(form.nome_lista || "").trim(),
        itens,
        observacao: String(form.observacao || "").trim(),
        enabled: !!form.enabled,
      };
      if (editing?.id) {
        await api.put(`/cid-authorizations/${editing.id}`, payload);
      } else {
        await api.post("/cid-authorizations", payload);
      }
      toast.success(editing ? "Lista atualizada e aplicada." : "Lista criada e aplicada.");
      close();
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Confirma exclusao?")) return;
    try {
      await api.delete(`/cid-authorizations/${id}`);
      toast.success("Lista removida.");
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="cid-authorizations-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Identidades permitidas</div>
          <h1 className="text-2xl font-semibold tracking-tight">Clientes e CIDs autorizados</h1>
        </div>
        <button className="sbc-btn sbc-btn-primary" onClick={() => open()}>
          <Plus size={14} /> Adicionar
        </button>
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Lista</th>
                <th>Clientes e CIDs</th>
                <th>Observacao</th>
                <th>Status</th>
                <th className="w-24 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center text-[color:var(--text-muted)] py-8">Carregando...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-[color:var(--text-muted)] py-8">Nenhum registro.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td className="font-medium">{row.nome_lista}</td>
                  <td className="text-xs">
                    {(row.itens || []).map((item) => {
                      const aparelho = [item.aparelho_fabricante, item.aparelho_modelo].filter(Boolean).join(" ");
                      return `${item.nome_cliente}: ${item.cid}${item.operadora_nome ? ` -> ${item.operadora_nome}` : ""}${aparelho ? ` (${aparelho})` : ""}`;
                    }).join(", ")}
                  </td>
                  <td>{row.observacao || "-"}</td>
                  <td>{row.enabled ? <span className="sbc-tag tag-online">ativo</span> : <span className="sbc-tag tag-offline">inativo</span>}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button className="sbc-btn" onClick={() => open(row)} title="Editar"><Edit3 size={12} /></button>
                      <button className="sbc-btn sbc-btn-danger" onClick={() => remove(row.id)} title="Excluir"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="sbc-card w-full max-w-5xl max-h-[92vh] p-0 overflow-hidden flex flex-col" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-[color:var(--border-default)] shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Novo"}</div>
                <h2 className="text-lg font-semibold">Clientes e CIDs autorizados</h2>
              </div>
              <button className="sbc-btn" onClick={close}><X size={14} /></button>
            </div>

            <form onSubmit={save} className="p-4 md:p-6 space-y-5 overflow-y-auto min-h-0">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Nome da lista</label>
                  <input className="sbc-input" value={form.nome_lista} onChange={(event) => setForm((current) => ({ ...current, nome_lista: event.target.value }))} placeholder="Clientes IPBX BR01" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Ativo</label>
                  <label className="flex items-center gap-2 pt-2">
                    <input type="checkbox" checked={!!form.enabled} onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} className="w-4 h-4" />
                    <span className="text-sm text-[color:var(--text-secondary)]">{form.enabled ? "Ativado" : "Desativado"}</span>
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Clientes e CIDs</div>
                    <div className="text-[11px] text-[color:var(--text-secondary)]">Defina o CID vindo do sipmbr e, se quiser, a operadora que o SBC deve usar na saida.</div>
                  </div>
                  <button type="button" className="sbc-btn" onClick={addItem}>
                    <Plus size={14} />
                    Incluir item
                  </button>
                </div>

                <div className="space-y-3">
                  {(form.itens || []).map((item, index) => (
                    <div key={`item-${index}`} className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_180px_minmax(0,1fr)_160px_180px_160px_52px] gap-3 items-end border border-[color:var(--border-default)] rounded-sm p-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Cliente</label>
                        <input
                          className="sbc-input"
                          value={item.nome_cliente}
                          onChange={(event) => updateItem(index, "nome_cliente", event.target.value)}
                          placeholder="Cliente Alfa"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">CID</label>
                        <input
                          className="sbc-input font-mono"
                          value={item.cid}
                          onChange={(event) => updateItem(index, "cid", event.target.value)}
                          placeholder="7135999160"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Operadora de saida</label>
                        <select
                          className="sbc-input"
                          value={item.operadora_id}
                          onChange={(event) => updateItem(index, "operadora_id", event.target.value)}
                        >
                          {operadoraOptions.map((option) => (
                            <option key={`${option.value}-${option.label}`} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Fabricante</label>
                        <select
                          className="sbc-input"
                          value={item.aparelho_fabricante || ""}
                          onChange={(event) => {
                            updateItem(index, "aparelho_fabricante", event.target.value);
                            updateItem(index, "sip_sanitize_profile", event.target.value === "Siemens" ? "siemens" : "");
                          }}
                        >
                          <option value="">Padrao</option>
                          <option value="Siemens">Siemens</option>
                          <option value="Intelbras">Intelbras</option>
                          <option value="Yealink">Yealink</option>
                          <option value="Grandstream">Grandstream</option>
                          <option value="Fanvil">Fanvil</option>
                          <option value="Outro">Outro</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Modelo</label>
                        <input
                          className="sbc-input"
                          value={item.aparelho_modelo || ""}
                          onChange={(event) => updateItem(index, "aparelho_modelo", event.target.value)}
                          placeholder="OpenStage 40"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Perfil SIP</label>
                        <select
                          className="sbc-input"
                          value={item.sip_sanitize_profile || ""}
                          onChange={(event) => updateItem(index, "sip_sanitize_profile", event.target.value)}
                        >
                          <option value="">Padrao</option>
                          <option value="siemens">Correcao Siemens</option>
                        </select>
                      </div>
                      <div className="flex justify-end">
                        <button type="button" className="sbc-btn sbc-btn-danger" onClick={() => removeItem(index)} title="Remover item">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">Observacao</label>
                <input className="sbc-input" value={form.observacao} onChange={(event) => setForm((current) => ({ ...current, observacao: event.target.value }))} />
              </div>

              {error && (
                <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
                  ! {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" className="sbc-btn" onClick={close}>Cancelar</button>
                <button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
